import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  Router,
} from '@angular/router';
import {
  SiaNewRouterService,
  SiaSessionService,
  SiaTokenStoreService,
  SiaUsersService,
  removeLocalStorageItems,
} from '@sia/shared';
import { Observable, lastValueFrom } from 'rxjs';
import { AuthService } from '../services/auth.service';

@Injectable({
  providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {
  readonly #router = inject(Router);
  readonly #authService = inject(AuthService);
  readonly #tokenStoreService = inject(SiaTokenStoreService);
  readonly #usersService = inject(SiaUsersService);
  readonly #routerService = inject(SiaNewRouterService);
  readonly #siaSessionService = inject(SiaSessionService);
  #isRedirectingToRoot = false;

  constructor() {
    // Reset redirect flag when navigation ends, is canceled, or errors
    this.#router.events.subscribe((evt) => {
      if (
        evt instanceof NavigationEnd ||
        evt instanceof NavigationCancel ||
        evt instanceof NavigationError
      ) {
        this.#isRedirectingToRoot = false;
      }
    });
  }

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return new Observable((observer) => {
      this.#handleAccess(req, next)
        .then((event) => {
          observer.next(event);
          observer.complete();
        })
        .catch((error: HttpErrorResponse) => {
          if (error.status === 499) {
            observer.complete();
          } else if (error.status === 401) {
            // if we're already redirecting, don't trigger another navigation unless
            // the current route belongs to the auth module (login/logout/etc).
            const currentUrl = this.#router.url;
            const isAuthRoute =
              currentUrl.includes('/auth') ||
              currentUrl.includes('two-factor-auth');

            if (!this.#isRedirectingToRoot && !isAuthRoute) {
              console.log(
                '%c401 detected, redirecting to login',
                'color: red; font-weight: bold;',
              );
              this.#isRedirectingToRoot = true;
              this.#handle401Error();
            } else {
              console.log(
                '%c401 detected, ignoring redirecting',
                'color: green; font-weight: bold;',
              );
            }

            // Emit error to stop execution flow in components/services
            observer.error(error);
          } else {
            observer.error(error);
          }
        });
    });
  }

  async #handle401Error(): Promise<void> {
    const currentUrl = this.#router.url;
    const isEmptyRoute =
      currentUrl === '/' || currentUrl === '' || currentUrl === '#';

    // Check if token is still valid
    const isTokenExpired =
      await this.#tokenStoreService.isCurrentTokenExpired();

    if (isEmptyRoute && !isTokenExpired) {
      // Token is valid but route is empty - navigate to user's default page
      const user = this.#usersService.getUserData();
      const defaultMenu =
        user?.redirectMasterMenuId || user?.redirectMobileMenuId || 'home-page';

      console.warn(
        'Empty route detected with valid token — redirecting to default page.',
      );
      await this.#routerService.navigateMenuWithParams(defaultMenu);
      return;
    }

    this.#routerService.clearRoutes();
    this.#siaSessionService.clearStorage();
    this.#tokenStoreService.clearToken();
    removeLocalStorageItems();
    console.warn('interceptor navigating to login');
    await this.#router.navigate(['']);
  }

  async #handleAccess(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Promise<HttpEvent<any>> {
    const token = this.#authService.getAccessToken();
    let changedRequest = request;
    if (token) {
      const headerSettings: { [name: string]: string | string[] } = {};
      for (const key of request.headers.keys()) {
        const all = request.headers.getAll(key);
        headerSettings[key] = all ?? [];
      }
      headerSettings['Authorization'] = 'Bearer ' + token;
      const newHeader = new HttpHeaders(headerSettings);
      changedRequest = request.clone({
        headers: newHeader,
      });
    }
    return lastValueFrom(next.handle(changedRequest));
  }
}
